"use client";

import React from "react";

import { format } from "date-fns";
import { ru, enUS, el } from "date-fns/locale";
import { useTranslations, useLocale } from "next-intl";

// Simplified interface without complex mappings
export interface TimelineEventProps {
  event: {
    _id: string;
    user?:
      | {
          name?: string;
          role?: string;
        }
      | string; // Handle both object and string userId
    timestamp: string;
    action: string;
    description?: string;
    details?: string; // Simplified field for additional info
  };
  isLast?: boolean;
}

// Get date-fns locale based on current locale
const getDateFnsLocale = (locale: string) => {
  switch (locale) {
    case "ru":
      return ru;
    case "el":
      return el;
    case "en":
    default:
      return enUS;
  }
};

// Simplified formatting without complex mappings
const formatEventAction = (
  event: TimelineEventProps["event"],
  tRealtor: (key: string) => string,
) => {
  // Map common actions to our realtor translations
  const actionMap: Record<string, string> = {
    created: "timelineCreated",
    updated: "timelineUpdated",
    status_change: "timelineStatusChange",
    assigned: "timelineAssigned",
    viewed: "timelineViewed",
    commented: "timelineCommented",
    archived: "timelineArchived",
  };

  const actionKey = actionMap[event.action];
  const actionText = actionKey ? tRealtor(actionKey) : event.action;

  // Extract user name safely
  let userName = "";
  if (typeof event.user === "string") {
    userName = event.user;
  } else if (event.user?.name) {
    userName = event.user.name;
  }

  // Use description or details directly from backend
  const details = event.details || event.description || "";

  return {
    actionText,
    userName,
    details,
  };
};

export const TimelineEvent: React.FC<TimelineEventProps> = ({
  event,
  isLast = false,
}) => {
  const tRealtor = useTranslations("realtor");
  const locale = useLocale();
  const dateFnsLocale = getDateFnsLocale(locale);

  const { actionText, userName, details } = formatEventAction(event, tRealtor);

  return (
    <div>
      <div className="flex flex-col justify-center">
        <div className="mb-1">
          <span
            className="text-[16px] font-bold text-[#444444] leading-[1.56] tracking-[0.2px]"
            style={{ fontFamily: "Geometria" }}
          >
            {format(new Date(event.timestamp), "dd.MM.yyyy HH:mm", {
              locale: dateFnsLocale,
            })}
          </span>
        </div>
        <div>
          <span
            className="text-[16px] font-normal text-[#444444] leading-[1.56] tracking-[0.2px]"
            style={{ fontFamily: "Geometria" }}
          >
            {actionText}
            {userName && (
              <>
                : <span className="font-bold">{userName}</span>
              </>
            )}
            {details && <>, {details}</>}
          </span>
        </div>
      </div>

      {!isLast && (
        <div className="my-6">
          <div className="bg-[#E5E5E5] h-px w-full" />
        </div>
      )}
    </div>
  );
};

export default TimelineEvent;
